import { useEffect, useMemo, useState } from "react";
import { buildClientRouteHighlightSegmentMap } from "../utils/routeModelsData.js";

function hasMountedSegmentLayer(mapState, layer) {
  return Boolean(mapState?.map)
    && mapState.isDisposed !== true
    && Boolean(layer)
    && mapState.map.hasLayer?.(layer) === true;
}

function applyRouteSegmentLayerStyles(mapState, segmentEntry, isHighlighted) {
  if (!segmentEntry || !mapState?.map || mapState.isDisposed) {
    return;
  }

  const styles = isHighlighted ? segmentEntry.highlightStyles : segmentEntry.defaultStyles;
  if (hasMountedSegmentLayer(mapState, segmentEntry.casing)) {
    segmentEntry.casing.setStyle?.(styles.casing);
  }
  if (hasMountedSegmentLayer(mapState, segmentEntry.line)) {
    segmentEntry.line.setStyle?.(styles.line);
  }
  segmentEntry.applyHighlightState?.(isHighlighted);

  if (isHighlighted) {
    if (hasMountedSegmentLayer(mapState, segmentEntry.casing)) {
      segmentEntry.casing.bringToFront?.();
    }
    if (hasMountedSegmentLayer(mapState, segmentEntry.line)) {
      segmentEntry.line.bringToFront?.();
    }
  }
}

function normalizeRouteSegmentIndex(segmentIndex) {
  const parsedIndex = Number.parseInt(segmentIndex, 10);
  return Number.isInteger(parsedIndex) && parsedIndex > 0 ? parsedIndex : null;
}

export function normalizeRouteSegmentIndexes(segmentIndexes = []) {
  const resolvedIndexes = Array.isArray(segmentIndexes) ? segmentIndexes : [segmentIndexes];
  const seenIndexes = new Set();

  return resolvedIndexes.reduce((indexes, segmentIndex) => {
    const normalizedIndex = normalizeRouteSegmentIndex(segmentIndex);
    if (!Number.isInteger(normalizedIndex) || seenIndexes.has(normalizedIndex)) {
      return indexes;
    }

    seenIndexes.add(normalizedIndex);
    indexes.push(normalizedIndex);
    return indexes;
  }, []);
}

export function syncHighlightedRouteSegments(mapState, highlightedIndexes = []) {
  if (!mapState?.map || mapState.isDisposed) {
    return;
  }

  const normalizedIndexes = normalizeRouteSegmentIndexes(highlightedIndexes);
  const highlightedIndexSet = new Set(normalizedIndexes);
  mapState.highlightedRouteSegmentIndexes = normalizedIndexes;
  mapState.highlightedRouteSegmentIndex = normalizedIndexes[0] ?? null;

  if (!Array.isArray(mapState.routeSegments)) {
    return;
  }

  mapState.routeSegments.forEach((segmentEntry) => {
    applyRouteSegmentLayerStyles(mapState, segmentEntry, highlightedIndexSet.has(segmentEntry.index));
  });
}

export default function useRouteHighlight({ assignment = null, mapStateRef, onHighlightChange = null, trip = null }) {
  const clientRouteSegmentMap = useMemo(
    () => buildClientRouteHighlightSegmentMap(trip, assignment),
    [assignment, trip],
  );
  const [highlightedRouteSegmentIndexes, setHighlightedRouteSegmentIndexes] = useState([]);
  const highlightedRouteSegmentIndex = highlightedRouteSegmentIndexes[0] ?? null;

  useEffect(() => () => {
    syncHighlightedRouteSegments(mapStateRef?.current || null, []);
    onHighlightChange?.(null);
  }, [mapStateRef, onHighlightChange]);

  useEffect(() => {
    setHighlightedRouteSegmentIndexes([]);
  }, [assignment, trip]);

  useEffect(() => {
    const normalizedIndexes = normalizeRouteSegmentIndexes(highlightedRouteSegmentIndexes);
    syncHighlightedRouteSegments(mapStateRef?.current || null, normalizedIndexes);
    onHighlightChange?.(normalizedIndexes[0] ?? null);
  }, [highlightedRouteSegmentIndexes, mapStateRef, onHighlightChange]);

  return {
    clearHighlight() {
      setHighlightedRouteSegmentIndexes([]);
    },

    highlightedRouteSegmentIndex,
    highlightedRouteSegmentIndexes,

    highlightClient(clientId) {
      if (!clientId) {
        return;
      }

      setHighlightedRouteSegmentIndexes(normalizeRouteSegmentIndexes(clientRouteSegmentMap.get(clientId)));
    },

    highlightRouteSegment(routeSegmentIndex) {
      setHighlightedRouteSegmentIndexes(normalizeRouteSegmentIndexes(routeSegmentIndex));
    },

    highlightRouteSegments(routeSegmentIndexes) {
      setHighlightedRouteSegmentIndexes(normalizeRouteSegmentIndexes(routeSegmentIndexes));
    },
  };
}
